import { DocumentV2, ListDocumentsResponse } from '@eigtech/documents-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { ROUTER_PATHS } from '@/lib/constants'
import { withRequiredPermissions } from '@/lib/permissions'
import { canEditDocument } from '@/lib/utils'
import { getUserCohort, useAuth0 } from '@eigtech/ui-shared-auth'
import { format } from '@eigtech/ui-shared-dates'
import {
  AspectRatio,
  Box,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  Center,
  ComposedAlert,
  ComposedCard,
  ConfirmModal,
  Divider,
  EmptyState,
  GridItem,
  GridLoader,
  Heading,
  Image,
  SimpleGrid,
  SkeletonWrapper,
  Stack,
  Text,
  Toolbar,
  chakra,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@eigtech/ui-shared-dave'
import {
  getDocumentDescription,
  getDocumentTitle,
  sortDocumentsByDate,
  useDeleteDocument,
  useGetDocumentUrl,
  useGetEntityDocuments,
} from '@eigtech/ui-shared-documents'
import {
  UpdateDocumentDrawer,
  mediaTypeRegex,
  photoTypeRegex,
} from '@eigtech/ui-shared-documents-web'
import { LinkButton } from '@eigtech/ui-shared-router'
import { createFileRoute } from '@tanstack/react-router'
import { groupBy } from 'lodash-es'
import { useMemo, useState } from 'react'
import { MdEdit as EditIcon, MdAddPhotoAlternate, MdDelete } from 'react-icons/md'
import { useMount } from 'react-use'

export const Route = createFileRoute('/_auth/jobs/$jobId/_layout/media')({
  component: () => <JobDetailMedia />,
})

const JobDetailMedia = withRequiredPermissions(BaseJobDetailMedia, {
  requiredPermissions: { action: 'read', subject: 'job' },
})

function BaseJobDetailMedia() {
  const { jobId } = Route.useParams()

  const { user } = useAuth0()

  // delegate component, default to technician cohort
  const cohort = getUserCohort(user) ?? 'technician'

  const { data, isPending, isError, refetch } = useGetEntityDocuments({
    cohort,
    entityId: ensureJobCorn(jobId),
  })

  useMount(() => {
    if (!jobId) return
    refetch()
  })

  const media = useMemo(() => {
    const documents = (data ?? []).filter((doc) => doc.filename?.match(mediaTypeRegex))

    const byDate = groupBy(documents, (media) => {
      const date = media.createdDate ?? media.modifiedDate
      const formattedDate = format(date ? new Date(date) : new Date(), 'MMMM d, yyyy')

      return formattedDate
    })

    const array = Object.entries(byDate).sort(
      (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()
    )
    const sorted = array.map(([key, dates]): [string, DocumentV2[]] => [
      key,
      [...dates].sort(sortDocumentsByDate),
    ])

    return sorted
  }, [data])

  const orientation = useBreakpointValue<ButtonGroupProps['orientation']>({
    base: 'vertical',
    md: 'horizontal',
  })

  return (
    <Stack spacing="6">
      <Stack>
        <Toolbar>
          <Stack
            alignItems={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
          >
            <Heading as="h3" size="md">
              Media
            </Heading>

            <ButtonGroup
              orientation={orientation}
              spacing="2"
              variant="outline"
              width={{ base: '100%', md: 'auto' }}
            >
              <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailPhotoReport.route}>
                Create Photo Report
              </LinkButton>
              <LinkButton
                params={{ jobId }}
                to={ROUTER_PATHS.jobDetailUploadDocuments.route}
                variant="solid"
              >
                Upload Media
              </LinkButton>
            </ButtonGroup>
          </Stack>
        </Toolbar>
      </Stack>

      <Stack divider={<Divider />} spacing="6">
        {isError ? (
          <ComposedAlert
            alert={{
              title: 'Something went wrong',
              description:
                'We could not load the media for this job. Please try refreshing. If the issue persists, please contact us for support.',
            }}
            status="error"
          />
        ) : isPending ? (
          <GridLoader
            columns={[1, 1, 2, 2, 3, 4]}
            numberOfSkeletonLoaders={5}
            skeletonProps={{ h: 320 }}
          />
        ) : media.length ? (
          media.map(([date, media]) => <MediaGrid key={date} date={date} media={media} />)
        ) : (
          <ComposedCard>
            <EmptyState heading="Get started by uploading media" icon={MdAddPhotoAlternate}>
              <Stack spacing="4">
                <Text>
                  If you have media you need to upload for your claim, you can do so by clicking the
                  button below.
                </Text>
                <Center>
                  <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailUploadDocuments.route}>
                    Upload Media
                  </LinkButton>
                </Center>
              </Stack>
            </EmptyState>
          </ComposedCard>
        )}
      </Stack>
    </Stack>
  )
}

function MediaGrid({ date, media }: { date: string; media: ListDocumentsResponse }) {
  return (
    <Stack>
      <Heading as="h4" size="md">
        {date}
      </Heading>

      <SimpleGrid columns={[1, 1, 2, 2, 3, 4]} gap={4}>
        {media.map((media) => (
          <GridItem key={media.id}>
            <Media {...media} />
          </GridItem>
        ))}
      </SimpleGrid>
    </Stack>
  )
}

function Media(document: DocumentV2) {
  const { entityId, filename, id } = document

  const { data: url, isPending, refetch } = useGetDocumentUrl({ entityId, documentId: id })

  useMount(() => {
    refetch()
  })

  return (
    <ComposedCard
      cardBodyProps={{ p: 0 }}
      cardFooterProps={{ flexGrow: 1, flexDir: 'column', alignItems: 'stretch' }}
      display="flex"
      flexDir="column"
      footer={
        <>
          <Stack flexGrow={1} flexShrink={0} mb="2">
            <Box>
              <Text textStyle="overline">Filename</Text>
              <Text wordBreak="break-word">{filename || '(None)'}</Text>
            </Box>
            <Box>
              <Text textStyle="overline">Title</Text>
              <Text wordBreak="break-word">{getDocumentTitle(document)}</Text>
            </Box>
            <Box>
              <Text textStyle="overline">Description</Text>
              <Text wordBreak="break-word">{getDocumentDescription(document) || '(None)'}</Text>
            </Box>
          </Stack>

          <MediaActions {...document} />
        </>
      }
      h="full"
    >
      {filename.match(photoTypeRegex) ? (
        <chakra.a href={url} target={id}>
          <AspectRatio objectFit="contain" ratio={4 / 3}>
            <SkeletonWrapper h="20" isLoaded={!isPending} w="full">
              <Image src={url} sx={{ objectFit: 'contain !important' }} w="full" />
            </SkeletonWrapper>
          </AspectRatio>
        </chakra.a>
      ) : (
        <Stack>
          <SkeletonWrapper isLoaded={!isPending} minH="20" w="full">
            <chakra.video controls src={url} w="full" />
          </SkeletonWrapper>
          <Button as="a" href={url} isDisabled={!url}>
            Open
          </Button>
        </Stack>
      )}
    </ComposedCard>
  )
}

export function MediaActions(document: DocumentV2) {
  const [isOpenUpdateDocumentDrawer, setIsOpenUpdateDocumentDrawer] = useState(false)

  const { mutateAsync: deleteDocument, isPending: isDeleting } = useDeleteDocument()

  const toast = useToast()
  const confirmModal = useDisclosure()

  async function confirmDeleteDocument() {
    await deleteDocument({ entityId: document.entityId, documentId: document.id })

    toast({
      status: 'success',
      title: 'Successfully deleted document',
    })
  }

  const { user } = useAuth0()
  const cohort = getUserCohort(user) ?? 'technician'
  const canEdit = canEditDocument(document.source, cohort)

  const orientation = useBreakpointValue<ButtonGroupProps['orientation']>({
    base: 'vertical',
    md: 'horizontal',
  })

  if (!canEdit) {
    return null
  }

  return (
    <>
      <ButtonGroup orientation={orientation} variant="outline">
        <Button
          rightIcon={<EditIcon />}
          w="full"
          onClick={() => setIsOpenUpdateDocumentDrawer(true)}
        >
          Edit Info
        </Button>

        <Button
          isLoading={isDeleting}
          rightIcon={<MdDelete />}
          w="full"
          onClick={confirmModal.onOpen}
        >
          Delete
        </Button>
      </ButtonGroup>

      {confirmModal.isOpen && (
        <ConfirmModal
          {...confirmModal}
          confirmLabel="Delete"
          confirmProps={{ colorScheme: 'red' }}
          isPending={isDeleting}
          title="Delete Document"
          onConfirm={confirmDeleteDocument}
        >
          Are you sure you want to delete this document?
        </ConfirmModal>
      )}

      {isOpenUpdateDocumentDrawer && (
        <UpdateDocumentDrawer
          document={document}
          open={isOpenUpdateDocumentDrawer}
          onOpenChange={setIsOpenUpdateDocumentDrawer}
        />
      )}
    </>
  )
}
